/**
 * Platform-neutral export content pipeline.
 *
 * No Node.js, Electron, or browser-DOM dependencies — pure TypeScript operating on
 * shared types and the existing ContentProcessor / formatter utilities.
 *
 * Platform services use this as their content engine, then handle the I/O themselves:
 *   Electron: BrowserWindow.printToPDF → dialog.showSaveDialog → fs.writeFile
 *   Web:      Packer.toBlob → URL.createObjectURL → <a download> / hidden iframe print
 */

import { BorderStyle, Document, Paragraph, Table, TextRun } from 'docx';
import { MessageModel, ExportFormat, ExportOptions } from '../types/models';
import {hasStructuredAssistantTags, isGemmaModel, isGPTOssModel, splitAssistantResponse} from './assistantResponseParser';
import { ContentProcessor } from './contentProcessor';

import {
    renderExportDocumentFromBlocks,
    renderReasoningBlock,
    renderCitationsBlock,
    renderImagesForMessage,
    formatMessageBlock,
    wrapDocument,
} from './exportFormatters';

// ─── Low-level helpers ────────────────────────────────────────────────────────

// docx  is handled upstream (generateExportContent branches before this).
export function renderContentForFormat(
    processor: ContentProcessor,
    format: ExportFormat,
): string {
    switch (format) {
        case 'pdf':
            return processor.generatePdfHtml();
        case 'markdown':
            return processor.generateMarkdown();
        case 'txt':
            return processor.generatePlainText();
        default:
            return processor.generatePdfHtml();
    }
}

/**
 * Build the role-header string for a single message in the target format.
 *
 * Returns format-appropriate output:
 * - `pdf`:      HTML div with inline icon and styled label
 * - `markdown`: Blockquote label (`> **User:**`) for clear visual separation
 * - `txt`:      Bracketed uppercase label (`[USER]`)
 *
 * @param assistantIcon  Data URL for the assistant icon image, or empty string.
 *                       Ignored for md/txt — only used in the PDF HTML path.
 *                       Electron resolves from packaged assets; web fetches via URL.
 * @param format         Target export format. Omit or pass `undefined` to get the
 *                       PDF/HTML output (backward-compatible default).
 */
export function buildRoleHeader(
    role: string,
    model: string,
    assistantIcon: string,
    showModel: boolean,
    format?: ExportFormat,
): string {
    if (format === 'markdown') {
        if (role === 'user') return '> **User:**';
        const modelSuffix = showModel && model ? ` \u2014 ${model}` : '';
        return `> **Assistant${modelSuffix}:**`;
    }
    if (format === 'txt') {
        if (role === 'user') return '[USER]';
        const modelSuffix = showModel && model ? ` - ${model}` : '';
        return `[ASSISTANT${modelSuffix}]`;
    }
    // PDF / HTML
    if (role === 'user') {
        const userIconSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style="display:inline-block;vertical-align:middle;">
  <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z"/>
</svg>`;
        return `<div class="message-role message-role.user">${userIconSvg} User</div>`;
    }
    return `<div class="message-role message-role.assistant">${assistantIcon ? `<img src="${assistantIcon}" alt="A" /> ` : ''}Assistant${showModel ? ` - ${model}` : ''}</div>`;
}

// ─── Message processing ───────────────────────────────────────────────────────

/**
 * Convert an array of MessageModel objects into format-specific content blocks.
 *
 * Returns one string per message — HTML divs for PDF, plain text paragraphs for
 * markdown/txt. These blocks are then assembled by generateExportContent.
 *
 * @param assistantIcon  Resolved data URL or empty string (platform provides this).
 */
export function processMessageBlocks(
    messages: MessageModel[],
    format: ExportFormat,
    options: ExportOptions,
    assistantIcon: string,
): { blocks: string[]; hasMath: boolean } {
    let hasMath = false;
    const blocks = messages.map((msg) => {
        if (!msg || typeof msg.content !== 'string') {
            console.warn('[exportPipeline] Invalid message structure:', msg);
            return '<div class="message-error">Invalid message content</div>';
        }

        let contentToExport = msg.content;
        let reasoningBlock = '';
        const includeReasoning = options.config?.includeReasoning ?? true;

        const assistantModel = msg.model || options.model;
        const isStructuredAssistant =
            msg.role === 'assistant' &&
            (isGPTOssModel(assistantModel) ||
                isGemmaModel(assistantModel) ||
                hasStructuredAssistantTags(msg.content));

        if (isStructuredAssistant) {
            const parsed = splitAssistantResponse(msg.content, assistantModel);
            if (includeReasoning && parsed.analysis) {
                reasoningBlock = renderReasoningBlock(parsed.analysis, format);
            }
            contentToExport = parsed.final || msg.content;
        }

        const processor = new ContentProcessor(contentToExport, {
            content: {
                includeCitations: options.config?.includeCitations ?? true,
                showModel: options.config?.showModel ?? true,
                showTitle: options.config?.showTitle ?? true,
                showRoleHeader: options.config?.showRoleHeader ?? true,
            },
        });

        // Skip tree walk once math is already detected in an earlier message
        if (!hasMath && processor.hasMath()) {
            hasMath = true;
        }

        const roleHeader =
            options.config?.showRoleHeader ?? true
                ? buildRoleHeader(
                      msg.role,
                      msg.model || options.model,
                      assistantIcon,
                      options.config?.showModel ?? true,
                      format,
                  )
                : '';

        const imagesBlock =
            msg.images && msg.images.length > 0
                ? renderImagesForMessage(
                      msg.images,
                      format,
                      msg.role,
                      options.config?.includeImages ?? true,
                  )
                : '';

        const renderedContent = renderContentForFormat(processor, format);

        const citationsBlock =
            msg.role !== 'user' && (options.config?.includeCitations ?? true)
                ? renderCitationsBlock(msg.citations || [], format)
                : '';

        const fullContent = reasoningBlock + renderedContent + citationsBlock;
        return formatMessageBlock(msg.role, roleHeader, imagesBlock, fullContent, format, options);
    });
    return { blocks, hasMath };
}

// ─── Content generation ───────────────────────────────────────────────────────

/**
 * Generate fully-assembled export content for a given format.
 *
 * For docx: returns a `docx.Document` instance ready for `Packer.toBuffer/toBlob`.
 * For all other formats: returns a string (HTML document for pdf, plain text for md/txt).
 *
 * @param assistantIconProvider  Async resolver for the assistant icon data URL.
 *   Electron: reads from packaged assets and returns a base64 data URL.
 *   Web: returns null (no filesystem access).
 *   Defaults to a no-op that returns null.
 *
 * @param logoProvider  Async resolver for the DOCX branding logo PNG bytes.
 *   Electron: reads from packaged assets.
 *   Web: returns null (no filesystem access).
 *   Defaults to a no-op that returns null.
 */
export async function generateExportContent(
    messages: MessageModel[],
    format: ExportFormat,
    options: ExportOptions,
    katexCss?: string,
    assistantIconProvider: () => Promise<string | null> = async () => null,
    logoProvider: () => Promise<Uint8Array | null> = async () => null,
): Promise<string | Document> {
    if (format === 'docx') {
        const profile = (options.config as any)?.docxProfile ?? 'generic';
        const docxBrandingEnabled =
            ((options.config as any)?.docxBrandingEnabled ?? true) && profile === 'generic';
        const docxBrandingAccent = (options.config as any)?.docxBrandingAccent ?? '#811411';
        const docxBrandingAccentSecondary =
            (options.config as any)?.docxBrandingAccentSecondary ?? '#962424';
        const docxBrandingLogoRenderPx =
            (options.config as any)?.docxBrandingLogoRenderPx ?? 48;
        const accentHex = docxBrandingAccent.replace('#', '').toUpperCase();
        const normalSizeHalfPoints = profile === 'memo' ? 24 : 22;
        const showRoleHeader = options.config?.showRoleHeader ?? true;
        const showModel = options.config?.showModel ?? true;
        const includeReasoning = options.config?.includeReasoning ?? true;

        const docxStyleOptions = {
            profile,
            branding: docxBrandingEnabled
                ? { enabled: true, accentColorHex: docxBrandingAccent, accentSecondaryColorHex: docxBrandingAccentSecondary }
                : { enabled: false as const },
        };

        // Build per-message paragraphs with role headers as explicit Paragraph objects.
        // This avoids encoding role headers in the markdown intermediate and then trying
        // to parse them back out — which is fragile and format-dependent.
        const allChildren: (Paragraph | Table)[] = [];

        messages.forEach((msg, i) => {
            if (!msg || typeof msg.content !== 'string') return;

            let contentToExport = msg.content;

            // Structured assistant output: split reasoning from final response.
            const assistantModel = msg.model || options.model;
            const isStructuredAssistant =
                msg.role === 'assistant' &&
                (isGPTOssModel(assistantModel) ||
                    isGemmaModel(assistantModel) ||
                    hasStructuredAssistantTags(msg.content));
            if (isStructuredAssistant) {
                const parsed = splitAssistantResponse(msg.content, assistantModel);
                if (includeReasoning && parsed.analysis) {
                    allChildren.push(
                        new Paragraph({
                            children: [new TextRun({ text: 'REASONING', bold: true, size: normalSizeHalfPoints, color: 'A3A3A3' })],
                            spacing: { after: 80 },
                        }),
                    );
                    new ContentProcessor(parsed.analysis)
                        .toDocxParagraphs(docxStyleOptions)
                        .forEach((p) => allChildren.push(p));
                }
                contentToExport = parsed.final || msg.content;
            }

            // Role header paragraph (bold label with accent left border)
            if (showRoleHeader) {
                const modelStr = msg.model || options.model;
                const label =
                    msg.role === 'user'
                        ? 'User'
                        : `Assistant${showModel && modelStr ? ` \u2014 ${modelStr}` : ''}`;
                allChildren.push(
                    new Paragraph({
                        children: [new TextRun({ text: label, bold: true, size: normalSizeHalfPoints })],
                        border: {
                            left: {
                                color: docxBrandingEnabled ? accentHex : 'AAAAAA',
                                style: BorderStyle.SINGLE,
                                size: 12,
                                space: 6,
                            },
                        },
                        spacing: { before: 200, after: 80 },
                    }),
                );
            }

            // Message content
            new ContentProcessor(contentToExport)
                .toDocxParagraphs(docxStyleOptions)
                .forEach((p) => allChildren.push(p));

            // Separator between messages (not after the last one)
            if (i < messages.length - 1) {
                allChildren.push(
                    new Paragraph({
                        children: [new TextRun({ text: '' })],
                        border: {
                            bottom: { color: 'AAAAAA', style: BorderStyle.SINGLE, size: 6, space: 1 },
                        },
                        spacing: { before: 120, after: 160 },
                    }),
                );
            }
        });

        let logoPng: Uint8Array | null = null;
        if (docxBrandingEnabled) {
            logoPng = await logoProvider();
        }

        // Pass prebuiltChildren to generateDocx — an empty ContentProcessor handles
        // only the Document wrapper (title, branding rule, page header/footer).
        return new ContentProcessor('').generateDocx(
            allChildren,
            {
                profile,
                includeHeader: options.config?.includeHeader ?? true,
                includeFooter: options.config?.includeFooter ?? true,
                title: options.title,
                headerText: options.headerText,
                footerText: options.footerText,
                branding: docxBrandingEnabled
                    ? {
                          enabled: true,
                          accentColorHex: docxBrandingAccent,
                          accentSecondaryColorHex: docxBrandingAccentSecondary,
                          logo: logoPng
                              ? { pngData: logoPng, renderPx: docxBrandingLogoRenderPx }
                              : undefined,
                      }
                    : { enabled: false },
            },
        );
    }

    const assistantIcon = (await assistantIconProvider()) ?? '';
    const { blocks, hasMath } = processMessageBlocks(messages, format, options, assistantIcon);

    switch (format) {
        case 'pdf':
            return renderExportDocumentFromBlocks(blocks, options, hasMath ? katexCss : undefined);
        case 'markdown':
        case 'txt':
            return wrapDocument(blocks, options, format);
        default:
            throw new Error(`[exportPipeline] Unsupported format: ${format}`);
    }
}
